import React, {useMemo} from 'react';
import {Typography} from '@mui/material';
import {t} from 'i18next';
import AurProgressChart, {
  ProgressChartDataSetType,
} from '../../../common/aur-progress-chart/AurProgressChart';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';
import {ReactComponent as InfoIcon} from '../../../../../src/assests/svg/info_icon.svg';
import {CustodianType} from '../ByCustodianTab';
import {convertStringToNumber} from '../../../common/aur-progress-chart/utills';
import {convertToDecimalPlace} from '../../../../utils/inTakeAuditReportUtils';
import AurDataGridLoadingOverlay from '../../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {TotalCumulativeVolumeReportType} from '../intakeAuditTypes';

const ReportDataTypeColorMap = {
  email: '#6E87FF',
  externallyProcessed: '#00D0DB',
  otherData: '#9F75DF',
};

const ReportDataTypeCaptionMap = {
  email: 'reports.intakeAudit.cumulativeReport.email',
  externallyProcessed:
    'reports.intakeAudit.cumulativeReport.externallyProcessed',
  otherData: 'reports.intakeAudit.cumulativeReport.otherData',
};

// Map each Cumulative Volume report to its corresponding custodian property name
const CustodianPropertyMap: Record<
  TotalCumulativeVolumeReportKey,
  keyof CustodianType
> = {
  email: 'emailCompressed',
  externallyProcessed: 'externallyProcessed',
  otherData: 'otherData',
};

type TotalCumulativeVolumeReportKey =
  | 'email'
  | 'externallyProcessed'
  | 'otherData';

export const MAXIMUM_FRACTION_DECIMAL_VOLUME = 3;

interface CumulativeVolumeReportChartProp {
  totalCumulativeReceivedVolume: TotalCumulativeVolumeReportType;
  custodianData: CustodianType[];
  hoverCustodian?: CustodianType | null;
  onRefresh?: () => void;
  isChartFailed?: boolean;
  isChartLoading?: boolean;
}

const CumulativeVolumeReportChart: React.FC<
  CumulativeVolumeReportChartProp
> = ({
  totalCumulativeReceivedVolume,
  custodianData,
  hoverCustodian = null,
  isChartFailed,
  isChartLoading,
  onRefresh,
}) => {
  const chartDataSet: ProgressChartDataSetType[] = useMemo(() => {
    // Initialize reportMap with total values and initial progress
    const reportMap: {[key: string]: ProgressChartDataSetType} = (
      Object.keys(
        totalCumulativeReceivedVolume
      ) as TotalCumulativeVolumeReportKey[]
    ).reduce(
      (acc, key) => {
        // Note: For fix bug ARHD-17377, no need to show progress of section which have total value 0
        if (
          convertStringToNumber(totalCumulativeReceivedVolume[key] ?? '0') === 0
        )
          return {...acc};
        return {
          ...acc,
          [key]: {
            total: convertStringToNumber(
              totalCumulativeReceivedVolume[key] ?? '0'
            ),
            progress: convertStringToNumber(
              totalCumulativeReceivedVolume[key] ?? '0'
            ),
            filledColor: ReportDataTypeColorMap[key],
            key,
          },
        };
      },
      {} as {[key: string]: ProgressChartDataSetType}
    );

    // Update progress for each custodian based on reportMap keys
    if (hoverCustodian) {
      // If hoverCustodian is provided, only calculate progress for that custodian
      Object.keys(reportMap).forEach((reportKey) => {
        const custodianProperty =
          CustodianPropertyMap[reportKey as TotalCumulativeVolumeReportKey];
        if (custodianProperty) {
          reportMap[reportKey].progress = convertStringToNumber(
            hoverCustodian[custodianProperty] ?? '0'
          );
        }
      });
    }

    return Object.values(reportMap).map((item) => {
      return {
        ...item,
        tooltip: {
          filled: (
            <Typography className="typo-text label2_size tooltip-content">
              {t(
                `reports.intakeAudit.cumulativeReport.barChartToolTip.${item.key}`
              )}
              <br />
              {hoverCustodian && (
                <>
                  {t(
                    'reports.intakeAudit.cumulativeReport.barChartToolTip.count',
                    {
                      value: convertToDecimalPlace(item.progress.toString()),
                    }
                  )}
                  <br />
                </>
              )}
              {t(
                'reports.intakeAudit.cumulativeReport.barChartToolTip.grandTotal',
                {
                  total: convertToDecimalPlace(item.total.toString()),
                }
              )}
            </Typography>
          ),
          unFilled: (
            <Typography className="typo-text label2_size tooltip-content">
              {t(
                `reports.intakeAudit.cumulativeReport.barChartToolTip.${item.key}`
              )}
              <br />
              {hoverCustodian && (
                <>
                  {t(
                    'reports.intakeAudit.cumulativeReport.barChartToolTip.count',
                    {
                      value: convertToDecimalPlace(
                        (item.total - item.progress).toString()
                      ),
                    }
                  )}
                  <br />
                </>
              )}
              {t(
                'reports.intakeAudit.cumulativeReport.barChartToolTip.grandTotal',
                {
                  total: convertToDecimalPlace(item.total.toString()),
                }
              )}
            </Typography>
          ),
        },
      };
    });
  }, [custodianData, totalCumulativeReceivedVolume, hoverCustodian]);

  const renderReportDataGuideItem = (
    key: TotalCumulativeVolumeReportKey,
    caption: string,
    tooltip?: string
  ) => {
    return (
      <div className="guide-content-card">
        <div className="row-full-width">
          <Typography
            aria-label={t(caption)}
            className="typo-text body4_size title-label"
          >
            {t(caption)}
          </Typography>
          {tooltip ? (
            <span className="tooltip-padding">
              <AurTooltip
                title={t(tooltip)}
                aria-label={t(tooltip)}
                className="typo-text body4_size tooltop-content"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -7],
                        },
                      },
                    ],
                  },
                }}
              >
                <InfoIcon tabIndex={0} />
              </AurTooltip>
            </span>
          ) : null}
        </div>
        <div className="guide-chart-row">
          <div className="bar-chart-container">
            <AurProgressChart
              dataSet={[
                {
                  total: 1,
                  progress: 1,
                  filledColor: ReportDataTypeColorMap[key],
                  key: key,
                },
              ]}
              layout={{
                barWidth: 5,
                barHeight: 19,
                spaceBetweenBar: 1,
              }}
            />
          </div>
          <Typography
            aria-label={`Total ${totalCumulativeReceivedVolume[key]} ${t('reports.hostingVolume.cumulativeReport.gbFor')} ${t(caption)}`}
            className="typo-text headline1_size volume-text"
          >
            {convertToDecimalPlace(
              totalCumulativeReceivedVolume[key] ?? '0.000'
            )}
            <span className="typo-text label1_size gb-caption">
              {t('reports.intakeAudit.cumulativeReport.gb')}
            </span>
          </Typography>
        </div>
      </div>
    );
  };

  const renderChartSection = () => {
    if (isChartLoading) {
      return <AurDataGridLoadingOverlay />;
    }

    if (isChartFailed) {
      return (
        <div className="chart-error">
          <AurDataGridErrorOverlay
            onRefresh={onRefresh}
            enableParentWrapper={false}
          />
        </div>
      );
    }

    return <AurProgressChart dataSet={chartDataSet} />;
  };

  return (
    <div className="cumulative-volume-card">
      <div className="header-content-row">
        <div className="title-row">
          <Typography
            aria-label={t('reports.intakeAudit.cumulativeReport.title')}
            className="typo-text subhead2_size title-color"
          >
            {t('reports.intakeAudit.cumulativeReport.title')}
          </Typography>
          <Typography
            aria-label={t('reports.intakeAudit.cumulativeReport.subTitle')}
            className="typo-text body3_size sub-title-text"
          >
            {t('reports.intakeAudit.cumulativeReport.subTitle')}
          </Typography>
        </div>
        <div className="chart-guide-row">
          {renderReportDataGuideItem(
            'email',
            ReportDataTypeCaptionMap['email'],
            'reports.intakeAudit.cumulativeReport.emailInfo'
          )}
          {renderReportDataGuideItem(
            'externallyProcessed',
            ReportDataTypeCaptionMap['externallyProcessed'],
            'reports.intakeAudit.cumulativeReport.externallyProcessedInfo'
          )}
          {renderReportDataGuideItem(
            'otherData',
            ReportDataTypeCaptionMap['otherData'],
            'reports.intakeAudit.cumulativeReport.otherDataInfo'
          )}
        </div>
      </div>
      <div className="aur-progress-chart-section">{renderChartSection()}</div>
    </div>
  );
};

export default CumulativeVolumeReportChart;
